import { useState, useEffect, useCallback, lazy } from 'react';
import { LazyShell } from './core/errors/LazyShell';
import { RoleGate } from './core/access/RoleGate';
import { ROUTES } from './core/routing/routes';
import Layout, { type LayoutChildProps } from './core/layout/Layout';
import { trackTabChange } from './analytics';
import { useSessionTimeout } from './core/auth/useSessionTimeout';
import { useCheckoutSuccess } from './core/auth/useCheckoutSuccess';
import { useVisualViewport } from './hooks/useVisualViewport';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { SESSION_WARNING_MS, SESSION_TIMEOUT_MS, LOGIN_PATH } from './constants';
import { useCurrentUser } from './core/auth/UserProvider';
import { on as onAppEvent, emit as emitAppEvent } from './core/events/bus';
import { parseNotificationUrl } from './core/notifications/route';
import CheckoutSuccessModal from './modules/user/CheckoutSuccessModal';
import {
    useNavigationState,
    type EducationSubTab as NavEducationSubTab,
    type NetworkingSubTab as NavNetworkingSubTab,
} from './core/navigation/useNavigationState';
import {
    TAB_TITLES,
    TAB_KEYS,
    type TabType as NavTabType,
} from './core/navigation/tabMetadata';
import { useConversationSeed } from './modules/messaging/useConversationSeed';
import { useAssistantChat } from './modules/chat/useAssistantChat';
import './App.css';

// Heavy tabs are lazy-loaded so they don't bloat the initial bundle
const NetworkingView    = lazy(() => import('./modules/networking/NetworkingView'));
const EducationView     = lazy(() => import('./modules/education/EducationView'));
const ProfileView       = lazy(() => import('./modules/user/ProfileView'));
const SettingsView      = lazy(() => import('./modules/user/SettingsView'));
const HomeView          = lazy(() => import('./modules/home/HomeView'));
const ConversationsView = lazy(() => import('./modules/messaging/ConversationsView'));
const ChatRoom          = lazy(() => import('./modules/chat/ChatRoom'));
// Why: ScheduleStubPage holds the "schedule" tab slot while the full
// CalendarAgendaSplit surface lands in a follow-up slice (see ScheduleStubPage.tsx).
const ScheduleStubPage  = lazy(() => import('./modules/schedule/ScheduleStubPage'));
// Phase 4.1 (Feature Surface Registry Gate): SubmissionsStubPage import
// removed — FEATURE_REGISTRY status 'hidden' (workflow-first 6-key rail
// pivot dropped Submissions). The file stays on disk until a follow-up
// cleanup slice deletes it; the rail/route entries are gone so users
// cannot reach it.
// Why: InsightsStubPage holds the "insights" tab slot while the full
// curated insights surface lands in a follow-up slice (see InsightsStubPage.tsx).
// eslint-disable-next-line chiefy/no-naked-concept-strings -- dynamic-import module specifier (filesystem path / infrastructure id), not user copy.
const InsightsStubPage = lazy(() => import('./modules/insights/InsightsStubPage'));

// Re-export navigation + DTO types so existing `import { ... } from
// '../../App'` call sites keep working while the SSOT lives elsewhere.
// TabType/EducationSubTab/NetworkingSubTab → core/navigation.
// Professional/Message (+ subtypes) → src/types.
export type TabType = NavTabType;
export type EducationSubTab = NavEducationSubTab;
export type NetworkingSubTab = NavNetworkingSubTab;
export type {
    Professional, ConnectionStatus,
    Message, MessageFile, MessageStatus, MessageUsage, ToolCall,
} from './types';

function App() {
    useVisualViewport();
    const userData = useCurrentUser();

    const {
        nav: { activeTab, educationSubTab, networkingSubTab, selectedChatId, selectedProjectId },
        setActiveTab,
        setSelectedChatId,
        setSelectedProjectId,
        handleTabChange,
    } = useNavigationState();
    useDocumentTitle(TAB_TITLES[activeTab] ?? null);

    const { initialActive, openDmWith, createAndOpenGroup } = useConversationSeed({
        onRouteToMessages: useCallback(() => setActiveTab(TAB_KEYS.CONVERSATIONS), [setActiveTab]),
    });

    const chat = useAssistantChat({
        selectedChatId,
        selectedProjectId,
        setSelectedChatId,
        setSelectedProjectId,
        onChatActivated: useCallback(() => setActiveTab('assistant'), [setActiveTab]),
    });

    const [sidebarProjects, setSidebarProjects] = useState<{ id: string; name: string }[]>([]);
    const [showSessionWarning, setShowSessionWarning] = useState(false);

    const handleSessionWarning = useCallback(() => setShowSessionWarning(true), []);
    const handleSessionTimeout = useCallback(() => {
        setShowSessionWarning(false);
        window.location.href = LOGIN_PATH;
    }, []);
    useSessionTimeout({
        warningAfterMs: SESSION_WARNING_MS,
        logoutAfterMs: SESSION_TIMEOUT_MS,
        onWarning: handleSessionWarning,
        onTimeout: handleSessionTimeout,
        enabled: !!userData,
    });

    // Sidebar broadcasts its loaded project list so ChatRoom's
    // move-to-project menu has immediate data.
    useEffect(() => {
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
        return onAppEvent('sidebar-projects-loaded', (payload) => {
            setSidebarProjects(payload?.projects ?? []);
        });
    }, []);

    // Education → Coaching bridge: when a mentee fires the "Reflect
    // in coaching" CTA from a lesson or bootcamp week, switch to the
    // Mentorship tab. MenteeMentorshipView subscribes to the same
    // event and opens MenteeActiveTask with the origin-lesson context.
    useEffect(() => {
        return onAppEvent('open-active-task', () => {
            setActiveTab('mentorship');
        });
    }, [setActiveTab]);

    // Notification deep-links: parse the URL the bell hands us into a
    // SPA action, switch tabs, and emit the take-over bus event so the
    // user lands directly on the relevant surface (no flash of a
    // dashboard root before the take-over mounts). Unrecognised URLs
    // gracefully degrade to a tab switch only.
    useEffect(() => {
        return onAppEvent('notification-clicked', (payload) => {
            const action = parseNotificationUrl(payload.url);
            if (!action) return;
            setActiveTab(action.tab);
            if (!('takeover' in action)) return;
            switch (action.takeover) {
                // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from NotificationAction union (infrastructure id), not user copy.
                case 'submission':
                    // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
                    emitAppEvent('open-submission-review', { submissionId: action.takeoverId });
                    break;
                // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from NotificationAction union (infrastructure id), not user copy.
                case 'report':
                    // No bus event for "open report by id" yet; the
                    // mentee dashboard refetches on mount and the
                    // user lands on Submissions where the row carries
                    // the deep-link entry. (When a dedicated event is
                    // wired, route here.)
                    break;
                // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value from NotificationAction union (infrastructure id), not user copy.
                case 'project':
                    // Same — when a per-side "open project by id"
                    // event is wired, route here.
                    break;
                case 'thread':
                    // Messages tab will surface the thread on its own
                    // because the inbox routes to the active thread
                    // when seeded; no extra event needed for v1.
                    break;
            }
        });
    }, [setActiveTab]);

    // Analytics: capture the landing tab on initial mount. Subsequent
    // tab changes fire `trackTabChange` from inside the navigation hook.
    useEffect(() => {
        trackTabChange(activeTab);
        // Why: mount-once analytics ping; adding activeTab to deps would
        // double-fire with the hook's own tab-change tracker.
        // see docs/mentorship-flow.md for the navigation event contract.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // After Stripe checkout the success handler redirects back here
    // with ?checkout=success.
    const { open: showCheckoutSuccess, close: closeCheckoutSuccess } = useCheckoutSuccess();

    const renderActiveView = ({ showMenu, onMenuClick }: LayoutChildProps) => {
        switch (activeTab) {
            case 'assistant':
                return (
                    <LazyShell scope="Assistant" routeKey="messaging">
                        <ChatRoom
                            messages={chat.messages}
                            placeholder="Ask Chiefy anything about property development..."
                            title={selectedChatId ? "Chiefy Assistant" : "New Chat"}
                            isLoadingMessages={chat.loadingMessages}
                            activeTab={activeTab}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            chatId={selectedChatId || undefined}
                            projectId={selectedProjectId || undefined}
                            projects={sidebarProjects}
                            onMoveToProject={chat.moveToProject}
                            onCreateProject={chat.createProjectFromChat}
                            onSendMessage={chat.sendMessage}
                            userData={userData}
                            chatState={chat.chatState}
                            onStop={chat.stopStream}
                            onRegenerate={chat.regenerateStream}
                        />
                    </LazyShell>
                );
            case 'networking':
                return (
                    <LazyShell scope="Networking" routeKey="networking">
                        <NetworkingView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onMessageUser={openDmWith}
                            initialSubTab={networkingSubTab}
                            onSubTabChange={(sub) => handleTabChange('networking', sub)}
                        />
                    </LazyShell>
                );
            case 'education':
                return (
                    <LazyShell scope="Education" routeKey="education">
                        <EducationView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            initialSubTab="bootcamp"
                        />
                    </LazyShell>
                );
            case 'mentorship':
                // Dedicated top-level entry. EducationView branches to
                // MentorshipView when subTab='coaching'. Role-gated so
                // users without any mentorship role get the forbidden
                // screen.
                return (
                    <RoleGate
                        route={ROUTES.mentorship}
                        user={userData}
                        onHome={() => handleTabChange('home')}
                        onMenuClick={onMenuClick}
                        showMenu={showMenu}
                    >
                        <LazyShell scope="Mentorship" routeKey="mentorship">
                            <EducationView
                                onMenuClick={onMenuClick}
                                showMenu={showMenu}
                                initialSubTab="coaching"
                            />
                        </LazyShell>
                    </RoleGate>
                );
            case 'profile':
                return (
                    <LazyShell scope="Profile" routeKey="profile">
                        <ProfileView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            userData={userData}
                            onNavigate={handleTabChange}
                        />
                    </LazyShell>
                );
            case 'settings':
                return (
                    <LazyShell scope="Settings" routeKey="settings">
                        <SettingsView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            userData={userData}
                            onNavigate={handleTabChange}
                        />
                    </LazyShell>
                );
            case 'home':
                return (
                    <LazyShell scope="Home" routeKey="home">
                        <HomeView
                            user={userData}
                            onNavigate={handleTabChange}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                        />
                    </LazyShell>
                );
            case 'conversations':
                return (
                    <LazyShell scope="Messages" routeKey="messaging">
                        <ConversationsView
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            initialActive={initialActive}
                            onCreateGroup={createAndOpenGroup}
                        />
                    </LazyShell>
                );
            case 'schedule':
                return (
                    <LazyShell scope="Schedule" routeKey="generic">
                        <ScheduleStubPage
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onNavigate={handleTabChange}
                        />
                    </LazyShell>
                );
            // Phase 4.1: 'submissions' case removed (FEATURE_REGISTRY status
            // 'hidden'). The TabType union no longer includes 'submissions',
            // so this branch was unreachable; the SubmissionsStubPage import
            // was retired with it. The default branch handles any residual
            // legacy localStorage value via the assistant fallback.
            case TAB_KEYS.INSIGHTS:
                return (
                    // eslint-disable-next-line chiefy/no-naked-concept-strings -- LazyShell `scope` prop is a Sentry/error-boundary tag (infrastructure id), not user copy.
                    <LazyShell scope="Insights" routeKey="generic">
                        <InsightsStubPage
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            onNavigate={handleTabChange}
                        />
                    </LazyShell>
                );
            default:
                // Unrecognised tab → assistant fallback, never blank.
                return (
                    <LazyShell scope="Assistant" routeKey="messaging">
                        <ChatRoom
                            messages={chat.messages}
                            placeholder="Ask Chiefy anything about property development..."
                            title="New Chat"
                            isLoadingMessages={chat.loadingMessages}
                            activeTab={activeTab}
                            onMenuClick={onMenuClick}
                            showMenu={showMenu}
                            chatId={selectedChatId || undefined}
                            projectId={selectedProjectId || undefined}
                            projects={sidebarProjects}
                            onMoveToProject={chat.moveToProject}
                            onCreateProject={chat.createProjectFromChat}
                            onSendMessage={chat.sendMessage}
                            userData={userData}
                        />
                    </LazyShell>
                );
        }
    };

    return (
        <>
            {showSessionWarning && (
                <div className="session-warning" role="alert">
                    <span>Your session is about to expire.</span>
                    <button type="button" onClick={() => setShowSessionWarning(false)}>
                        Stay signed in
                    </button>
                </div>
            )}

            <Layout
                activeTab={activeTab}
                onTabChange={handleTabChange}
                educationSubTab={educationSubTab}
                networkingSubTab={networkingSubTab}
                userData={userData}
                onChatSelect={chat.selectChat}
                onNewDirectChat={chat.newDirectChat}
                selectedChatId={selectedChatId || undefined}
                selectedProjectId={selectedProjectId || undefined}
            >
                {(props) => renderActiveView(props)}
            </Layout>

            <CheckoutSuccessModal
                open={showCheckoutSuccess}
                onClose={closeCheckoutSuccess}
                planName={userData?.active_plan?.plan?.name}
            />
        </>
    );
}

export default App;
